import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    navigate("/");
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleVideosClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    navigate("/videos");
  };

  const handleShopClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    navigate("/shop");
  };

  return (
    <div className="min-h-screen">
      {/* Fixed Header */}
      <header className="bg-gradient-to-r from-sky-50 to-blue-50 shadow-lg fixed top-0 left-0 right-0 z-50 h-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <button onClick={handleHomeClick} className="flex items-center gap-3 sm:gap-4 cursor-pointer">
            <img
              src="https://static.lumi.new/material/a9/a98ddac5f5da58315caa1c51a6d4a5ac.jpg"
              alt="Tiny Tots Sandbox Logo"
              className="h-12 w-12 rounded-full shadow-lg border-2 border-white object-cover flex-shrink-0" />

            <h1
              className="text-lg sm:text-2xl md:text-3xl font-extrabold tracking-wide"
              style={{
                fontFamily: "'Comic Sans MS', 'Chalkboard SE', 'Arial Rounded MT Bold', -apple-system, BlinkMacSystemFont, sans-serif"
              }}>
              <span style={{ color: '#EF4444' }}>T</span>
              <span style={{ color: '#3B82F6' }}>i</span>
              <span style={{ color: '#FFD93D' }}>n</span>
              <span style={{ color: '#A8E063' }}>y</span>
              <span style={{ color: '#FF6B35' }}> </span>
              <span style={{ color: '#EF4444' }}>T</span>
              <span style={{ color: '#3B82F6' }}>o</span>
              <span style={{ color: '#FFD93D' }}>t</span>
              <span style={{ color: '#A8E063' }}>s</span>
              <span style={{ color: '#FF6B35' }}> </span>
              <span style={{ color: '#EF4444' }}>S</span>
              <span style={{ color: '#3B82F6' }}>a</span>
              <span style={{ color: '#FFD93D' }}>n</span>
              <span style={{ color: '#A8E063' }}>d</span>
              <span style={{ color: '#FF6B35' }}>b</span>
              <span style={{ color: '#EF4444' }}>o</span>
              <span style={{ color: '#3B82F6' }}>x</span>
            </h1>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {/* Navigation hidden for Coming Soon phase */}
          </nav>

          {/* Hamburger menu hidden for Coming Soon phase */}
        </div>

        {/* Mobile Menu Overlay - Hidden for Coming Soon phase */}
      </header>

      {/* Page Content with Top Padding */}
      <div className="pt-20">
        {children}
      </div>

      {/* Global Footer */}
      <footer className="bg-gradient-to-r from-sky-500 via-lime-500 to-yellow-400 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xl md:text-2xl font-bold" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            © 2026 Tiny Tots Sandbox - Where Imagination Meets Education! ✨
          </p>
        </div>
      </footer>
    </div>);

}

function NewsletterSection() {
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  return (
    <section className="bg-white py-16">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-yellow-100 rounded-3xl border-4 border-dashed border-yellow-400 p-8 md:p-12 text-center shadow-lg">
          <h2 className="text-3xl md:text-4xl font-black text-yellow-600 mb-4" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            Join the Sandbox Family! 🎉
          </h2>
          <p className="text-lg md:text-xl text-gray-700 mb-8 font-semibold">
            Get a <span className="text-yellow-600 font-black">FREE 3-page printable coloring pack</span> instantly when you join! Plus, you'll receive our monthly newsletter with a new freebie every month and exclusive early-access alerts for our upcoming storybooks, custom apparel, and our curated boutique of nursery essentials.
          </p>
          {!newsletterSubmitted ?
          <form
            className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto"
            onSubmit={async (e) => {
              e.preventDefault();
              setError("");
              setIsSubmitting(true);
              
              const formData = new FormData(e.currentTarget);
              const email = formData.get("email") as string;
              
              try {
                const response = await fetch("https://api.lumi.new/v1/functions/p414092322000683008/addSubscriber", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json"
                  },
                  body: JSON.stringify({ email })
                });
                
                const data = await response.json();
                
                if (!response.ok) {
                  throw new Error(data.error || "Failed to subscribe");
                }
                
                setNewsletterSubmitted(true);
              } catch (err: any) {
                console.error("Subscription error:", err);
                setError(err.message || "Something went wrong. Please try again.");
              } finally {
                setIsSubmitting(false);
              }
            }}>

              <input
              type="email"
              name="email"
              placeholder="Enter your email address"
              className="flex-1 px-6 py-4 rounded-full border-4 border-yellow-300 text-gray-800 text-lg font-semibold focus:outline-none focus:border-lime-500 transition-colors duration-300"
              required 
              disabled={isSubmitting} />

              <button
              type="submit"
              disabled={isSubmitting}
              className="bg-lime-500 hover:bg-lime-600 text-white font-black text-lg px-8 py-4 rounded-full shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

                {isSubmitting ? "Joining..." : "Join the Family"}
              </button>
            </form> :

          <div
            className="text-center py-6 animate-fade-in"
            style={{ animation: "fadeIn 0.6s ease-in" }}>

              <p className="text-2xl md:text-3xl font-black text-lime-600" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
                Welcome to the Family! Check your inbox for your free coloring pack. 🎨
              </p>
            </div>
          }
          {error && (
            <p className="text-red-600 font-bold text-center mt-4">{error}</p>
          )}
        </div>
      </div>
    </section>);

}

export default Layout;